import type {
  RawData,
  TestDefinition,
  TestResult,
  ValidationResult,
} from '../types.ts';
import {
  errorText,
  formatError,
  hash,
  result,
  validation,
  validationError,
} from './shared.ts';

async function collect(): Promise<RawData> {
  const OfflineContext = window.OfflineAudioContext;
  if (!OfflineContext) {
    throw new Error('OfflineAudioContext is unavailable');
  }
  const context = new OfflineContext(1, 5000, 44_100);
  const oscillator = context.createOscillator();
  const compressor = context.createDynamicsCompressor();
  oscillator.type = 'triangle';
  oscillator.frequency.value = 1000;
  oscillator.connect(compressor);
  compressor.connect(context.destination);
  oscillator.start(0);
  const buffer = await context.startRendering();
  const samples = [...buffer.getChannelData(0).slice(4500, 5000)];
  const silenceContext = new OfflineContext(1, 128, 44_100);
  const silence = await silenceContext.startRendering();
  const silenceValues = [...silence.getChannelData(0).slice(0, 8)];

  return {
    context: {
      sampleRate: buffer.sampleRate,
      length: buffer.length,
      duration: buffer.duration,
      channels: buffer.numberOfChannels,
    },
    offlineHash: await hash(samples),
    compressorSamples: samples.slice(0, 16),
    silence: {
      float: silenceValues,
    },
  };
}

function format(rawData: RawData): string {
  const error = formatError(rawData);
  if (error) {
    return error;
  }
  return typeof rawData.offlineHash == 'string'
    ? rawData.offlineHash
    : 'Captured';
}

async function run(): Promise<TestResult> {
  let rawData: RawData;
  try {
    rawData = await collect();
  } catch (error) {
    rawData = { error: errorText(error) };
  }
  return result(rawData, format);
}

function validate(rawData: RawData): ValidationResult {
  const error = validationError(rawData);
  if (error) {
    return error;
  }
  return /^[0-9a-f]{32}$/.test(String(rawData.offlineHash))
    ? validation([])
    : validation(['Audio output hash is missing or invalid']);
}

export const audioTest: TestDefinition = {
  id: 'audioFingerprint',
  title: 'Audio fingerprint',
  category: 'shared',
  format,
  run,
  validate,
};
